/**
 * „Fragebögen" — Liste + Upload (Pipeline-Stufen 1-3 aus Sicht des Nutzers).
 *
 * Muster: `pages/Documents.tsx` (Dropzone, `postForm`, Polling im Intervall,
 * Folge-Aktion erst freigeschaltet, wenn der Lauf durch ist). Zwei Unterschiede,
 * beide fachlich:
 *
 * 1. **Kunde und Produkt werden BEIM Upload erfragt.** Das Produkt entscheidet,
 *    welche Fakten die Synthese auflösen kann (`{{product.inci}}`) — es später
 *    nachzutragen hiesse, den ganzen Lauf zu wiederholen.
 * 2. **`error_text` und die Ablehnung stehen in der Liste**, nicht hinter einem
 *    Klick. „Das ist kein Fragebogen, sondern ein Quality Agreement" ist die
 *    Antwort des Systems auf eine Frage des Nutzers und gehört dorthin, wo er
 *    hinsieht.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, FileText, Loader2, Trash2, Upload } from "lucide-react";
import clsx from "clsx";

import { api, postForm } from "../api";
import { useT } from "../i18n";
// `katalog()` statt `t` überall dort, wo der Text NACH einem `await` (oder aus
// einem einmal aufgesetzten Intervall) gebraucht wird: der Hook-Wert wäre dort
// der Stand vom Render-Zeitpunkt (docs/i18n.md §3).
import { katalog } from "../i18n/lang";
import { Button, Card, EmptyState, Field, Input, PageHeader, Select, StatusBadge, Tabs } from "../ui";
import { useModal } from "../ui/modal";
import { useToast } from "../ui/toast";
import { fragebogenApi, type Fragebogen } from "./api";
import { datum, laufBeschriftung, statusInfo } from "./labels";
import DokumentenRegistry from "./DokumentenRegistry";

interface ProduktFakt {
  product_slug: string;
}

export default function FragebogenUebersicht() {
  const [reiter, setReiter] = useState<"fragebogen" | "dokumente">("fragebogen");
  const [liste, setListe] = useState<Fragebogen[] | null>(null);
  const [produkte, setProdukte] = useState<string[]>([]);
  const [kunde, setKunde] = useState("");
  const [produkt, setProdukt] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const dateiFeld = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const modal = useModal();
  const navigate = useNavigate();
  const t = useT().questionnaires;

  const laden = async () => {
    try {
      setListe(await fragebogenApi.liste());
    } catch (e) {
      toast.err(katalog().questionnaires.listeFehlgeschlagen, (e as Error).message);
      setListe([]);
    }
  };

  useEffect(() => {
    laden();
    api
      .get<ProduktFakt[]>("/api/questionnaires/product-facts")
      .then((rows) => {
        const slugs = Array.from(new Set(rows.map((r) => r.product_slug))).sort();
        setProdukte(slugs);
      })
      .catch(() => setProdukte([]));
  }, []);

  // Nachladen, solange irgendein Lauf noch arbeitet — dasselbe Muster wie in
  // Documents.tsx (`setInterval(4000)`, Zustand aus dem Setter gelesen, damit
  // das Intervall nicht bei jedem Render neu aufgesetzt werden muss).
  useEffect(() => {
    const t = setInterval(() => {
      setListe((aktuell) => {
        if (aktuell?.some((f) => statusInfo(f.status).laeuft)) laden();
        return aktuell;
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const hochladen = async (datei: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", datei);
      if (kunde.trim()) fd.append("customer", kunde.trim());
      if (produkt) fd.append("product_slug", produkt);
      const neu = await postForm<Fragebogen>("/api/questionnaires/", fd);
      const tt = katalog().questionnaires;
      toast.ok(tt.hochgeladen, tt.wirdGelesen(neu.filename));
      await laden();
    } catch (e) {
      toast.err(katalog().questionnaires.uploadFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
      setOver(false);
      if (dateiFeld.current) dateiFeld.current.value = "";
    }
  };

  const loeschen = async (f: Fragebogen) => {
    const ok = await modal.confirm({
      title: t.loeschenTitel(f.filename),
      description: t.loeschenBeschreibung,
      confirmLabel: t.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await fragebogenApi.loeschen(f.id);
      setListe((s) => (s ?? []).filter((x) => x.id !== f.id));
      toast.ok(katalog().questionnaires.geloescht);
    } catch (e) {
      toast.err(katalog().questionnaires.loeschenFehlgeschlagen, (e as Error).message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <PageHeader title={t.titel} description={t.beschreibung} />

      <Tabs
        value={reiter}
        onChange={(id) => setReiter(id as "fragebogen" | "dokumente")}
        items={[
          { id: "fragebogen", label: t.reiterFragebogen },
          { id: "dokumente", label: t.reiterDokumente },
        ]}
      />

      {reiter === "dokumente" ? (
        <DokumentenRegistry />
      ) : (
        <>
      <Card padding="sm" className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t.kunde} optional>
            <Input
              value={kunde}
              onChange={(e) => setKunde(e.target.value)}
              placeholder={t.kundePlatzhalter}
            />
          </Field>
          <Field label={t.produkt} optional hint={t.produktHinweis}>
            <Select value={produkt} onChange={(e) => setProdukt(e.target.value)}>
              <option value="">{t.keinProduktbezug}</option>
              {produkte.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) hochladen(f);
          }}
          className={clsx(
            "block border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors",
            over
              ? "border-brand-600 bg-brand-50/50 dark:bg-brand-700/10"
              : "border-border-strong hover:border-brand-500"
          )}
        >
          <Upload className="w-7 h-7 text-brand-500 mx-auto mb-2" />
          <div className="text-sm font-medium">{t.ablegeZone}</div>
          <div className="text-xs text-text-2 mt-1">{t.ablegeFormate}</div>
          <input
            ref={dateiFeld}
            type="file"
            accept=".pdf,.docx,.xlsx"
            aria-label={t.dateiAria}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) hochladen(f);
            }}
            className="sr-only"
          />
          {busy && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-text-2">
              <Loader2 className="w-3 h-3 animate-spin" /> {t.wirdHochgeladen}
            </div>
          )}
        </label>
      </Card>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2">
          {t.laeufe}{" "}
          <span className="text-text-3 font-normal">({liste?.length ?? 0})</span>
        </h2>

        {liste === null && (
          <Card padding="sm" className="text-sm text-text-2 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {t.wirdGeladen}
          </Card>
        )}

        {liste?.length === 0 && (
          <Card padding="none">
            <EmptyState
              icon={<ClipboardList className="w-6 h-6" />}
              title={t.leerTitel}
              hint={t.leerHinweis}
              action={
                <Button variant="secondary" onClick={() => dateiFeld.current?.click()}>
                  {t.dateiWaehlen}
                </Button>
              }
            />
          </Card>
        )}

        {liste?.map((f) => (
          <FragebogenZeile
            key={f.id}
            f={f}
            onOeffnen={() => navigate(`/fragebogen/${f.id}`)}
            onLoeschen={() => loeschen(f)}
          />
        ))}
      </section>
        </>
      )}
    </div>
  );
}

function FragebogenZeile({
  f,
  onOeffnen,
  onLoeschen,
}: {
  f: Fragebogen;
  onOeffnen: () => void;
  onLoeschen: () => void;
}) {
  const t = useT().questionnaires;
  const info = statusInfo(f.status);
  const offen = f.status === "review" || f.status === "approved" || f.status === "filled";
  return (
    <Card padding="sm" className="group">
      <div className="flex items-start gap-3">
        <FileText className="w-4 h-4 text-text-2 mt-1 shrink-0" />
        <button
          onClick={onOeffnen}
          disabled={!offen}
          className="flex-1 min-w-0 text-left disabled:cursor-default"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={clsx(
                "text-sm font-medium truncate",
                offen && "group-hover:text-brand-600"
              )}
            >
              {f.filename}
            </span>
            <StatusBadge status={info.status}>
              {info.laeuft && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              {/* Stufe UND Zähler: „Antworten werden gesucht — 23/70". Ohne die
                  Zahl steht hier bei einem grossen Bogen minutenlang derselbe
                  Satz, und niemand kann sehen, ob sich noch etwas bewegt. */}
              {laufBeschriftung(f.status, f.fortschritt)}
            </StatusBadge>
          </div>
          <div className="text-xs text-text-2 mt-1 flex items-center gap-2 flex-wrap">
            {f.customer && <span>{f.customer}</span>}
            {f.product_slug && <span>· {f.product_slug}</span>}
            <span>· {t.fragenAnzahl(f.question_count)}</span>
            <span>· {datum(f.created_at)}</span>
            <span>· {f.created_by}</span>
          </div>
          {f.error_text && (
            <div
              className={clsx(
                "mt-2 text-xs rounded-md px-2 py-1.5",
                f.status === "error"
                  ? "bg-danger-soft text-danger"
                  : "bg-warning-soft text-warning"
              )}
            >
              {f.error_text}
            </div>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {offen && (
            <Button size="sm" variant="secondary" onClick={onOeffnen}>
              {t.pruefen}
            </Button>
          )}
          <button
            onClick={onLoeschen}
            title={t.fragebogenLoeschen}
            aria-label={t.fragebogenLoeschenAria(f.filename)}
            className="p-1.5 text-text-2 hover:text-danger hover:bg-danger-soft rounded-md"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}
